// Helpers for handling values that arrive from the URL.
//
// Anything in the query string is attacker-controlled: a link such as
// /login?error=Your%20account%20is%20locked,%20call%20+1-555... renders text of
// the attacker's choosing on our own sign-in page. React escapes HTML so this is
// not XSS, but the wording still borrows our domain's credibility, so query
// values that reach the UI are normalised and capped first.

/** Longest message we will echo back from a query parameter. */
export const MAX_MESSAGE_PARAM_LENGTH = 160;

/** ASCII control characters, which can be used to fake line breaks in a message. */
const CONTROL_CHARS = /\p{Cc}/gu;

/** Markup / template characters that have no place in a plain status message. */
const MARKUP_CHARS = /[<>{}\\`]/g;

/** Embedded links — the usual payload when a message is used for phishing. */
const EMBEDDED_URLS = /\b(?:https?:\/\/|www\.)\S*/gi;

/**
 * Makes a URL-supplied message safe to display: drops markup, control characters
 * and embedded links, collapses whitespace and truncates. Returns '' for empty
 * input.
 */
export const sanitizeMessageParam = (raw: string | null | undefined): string => {
  if (!raw) return '';
  return raw
    .replace(MARKUP_CHARS, '')
    .replace(CONTROL_CHARS, ' ')
    .replace(EMBEDDED_URLS, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_MESSAGE_PARAM_LENGTH);
};

/**
 * Shape check for a JWT before it is persisted: three base64url segments. The
 * signature can only be verified by the API — this just stops junk or
 * hand-crafted strings from being written to storage as if they were a session.
 */
export const looksLikeJwt = (value: string | null | undefined): boolean =>
  !!value && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/.test(value);
